import { Link, useSearchParams } from "react-router-dom";
import { useLogin } from "../../../hooks/auth/useLogin";
import { authFormSchema, type AuthFormInput } from "../../../utils/zod/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller } from "react-hook-form";
import { InputField } from "../ui/inputField";
import { PasswordField } from "../ui/passwordField";
import { ThemedLogo } from "../ui/themedLogo";
import { SelectField } from "../ui/select/selectField";

const FEATURES = [
  {
    label: "Gestión de socios",
    description: "Altas, bajas y renovaciones en segundos",
    icon: (
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.75}
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
    ),
  },
  {
    label: "Pagos y suscripciones",
    description: "Control total de cobros y planes",
    icon: (
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.75}
          d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
        />
      </svg>
    ),
  },
  {
    label: "Métricas y reportes",
    description: "Analítica financiera en tiempo real",
    icon: (
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.75}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
      </svg>
    ),
  },
  {
    label: "Inventario y ventas",
    description: "Stock de productos siempre al día",
    icon: (
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.75}
          d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
        />
      </svg>
    ),
  },
];

export const LoginForm = () => {
  const [searchParams] = useSearchParams();
  const resetSuccess = searchParams.get("reset") === "success";
  const { mutate, isPending, availableGyms, resetGymSelection } = useLogin();
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors, isSubmitted },
  } = useForm<AuthFormInput>({
    resolver: zodResolver(authFormSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
      gymSlug: "",
    },
  });

  const onSubmit = (data: AuthFormInput) =>
    mutate(data, {
      onSuccess: () => {
        reset();
        resetGymSelection();
      },
    });

  const gymSlug = watch("gymSlug");
  const requiresGymSelection =
    Array.isArray(availableGyms) && availableGyms.length > 0;
  const gymSelectionError =
    requiresGymSelection && isSubmitted && !gymSlug
      ? "Selecciona un gimnasio para continuar"
      : undefined;

  return (
    <div className="w-full max-w-3xl px-3 sm:px-4">
      <div className="bg-global-overlay backdrop-blur-sm border border-global-primary shadow-global-card rounded-2xl overflow-hidden zoom-in-95">
        <div className="flex min-h-[500px]">
          {/* ── Left branding panel (desktop only) ── */}
          <div className="hidden md:flex flex-col justify-between w-[42%] bg-global-primary border-r border-global-primary p-8 lg:p-10">
            <div>
              <ThemedLogo className="h-10 w-auto" />

              <div className="mt-8">
                <h1 className="text-lg font-bold text-global-title leading-snug">
                  Gestión inteligente
                  <br />
                  para tu negocio
                </h1>
                <p className="mt-2 text-xs text-global-accent leading-relaxed">
                  Administra tu gimnasio de forma simple y eficiente.
                </p>
              </div>

              <ul className="mt-7 space-y-4">
                {FEATURES.map((f) => (
                  <li key={f.label} className="flex items-start gap-3">
                    <span className="mt-0.5 shrink-0 w-7 h-7 rounded-lg bg-global-overlay-secondary border border-global-secondary flex items-center justify-center text-global-accent">
                      {f.icon}
                    </span>
                    <div>
                      <p className="text-xs font-semibold text-global-title">
                        {f.label}
                      </p>
                      <p className="text-xs text-global-muted">
                        {f.description}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="inline-flex items-center gap-1.5 mt-6 px-3 py-1.5 rounded-full bg-global-overlay-secondary border border-global-primary w-fit">
              <svg
                className="w-3 h-3 text-global-accent shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <span className="text-xs text-global-muted">
                Conexión cifrada SSL
              </span>
            </div>
          </div>

          {/* ── Right form panel ── */}
          <div className="flex-1 flex flex-col justify-center px-6 sm:px-8 md:px-9 py-8 md:py-10">
            {/* Mobile logo */}
            <div className="flex flex-col items-center mb-7 md:hidden">
              <ThemedLogo className="h-10 w-auto" />
            </div>

            <div className="mb-7">
              <h2 className="text-xl font-bold tracking-tight text-global-title">
                Iniciar sesión
              </h2>
              <p className="mt-1 text-sm text-global-accent">
                Ingresa tus credenciales para continuar
              </p>
            </div>

            {resetSuccess && (
              <div className="mb-5 p-3 rounded-lg bg-green-500/20 border border-green-500/40 text-green-200 text-xs">
                Contraseña restablecida exitosamente. Inicia sesión con tu nueva
                contraseña.
              </div>
            )}

            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-1.5">
                <label
                  htmlFor="login-email"
                  className="block text-xs font-medium text-global-label"
                >
                  Correo electrónico
                </label>
                <InputField
                  id="login-email"
                  type="email"
                  placeholder="tu@correo.com"
                  {...register("email")}
                />
                {errors.email && (
                  <p className="text-size-2xs text-global-error">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label
                    htmlFor="login-password"
                    className="block text-xs font-medium text-global-label"
                  >
                    Contraseña
                  </label>
                  <Link
                    to="/auth/forgot-password"
                    className="text-xs text-global-accent hover:text-global-title transition-colors duration-200 hover:underline"
                  >
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <PasswordField
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="••••••••"
                    />
                  )}
                />
                {errors.password && (
                  <p className="text-size-2xs text-global-error">
                    {errors.password.message}
                  </p>
                )}
              </div>

              {requiresGymSelection && (
                <div className="space-y-1.5 rounded-lg border border-global-primary bg-global-overlay-secondary p-3">
                  <p className="text-xs text-global-accent">
                    Tu correo está registrado en más de un gimnasio. Elegí a
                    cuál querés ingresar.
                  </p>
                  <SelectField
                    id="login-gym-slug"
                    label={
                      <span className="text-xs font-medium text-global-label">
                        Gimnasio
                      </span>
                    }
                    options={[
                      { value: "", label: "Selecciona un gimnasio" },
                      ...availableGyms!.map((g) => ({
                        value: g.slug,
                        label: g.name,
                      })),
                    ]}
                    error={gymSelectionError}
                    {...register("gymSlug")}
                  />
                </div>
              )}

              <label
                htmlFor="remember-me"
                className="flex items-center gap-2.5 cursor-pointer select-none group"
              >
                <div className="relative shrink-0 w-4 h-4">
                  <input
                    type="checkbox"
                    id="remember-me"
                    {...register("rememberMe")}
                    className="peer sr-only"
                  />
                  <span className="absolute inset-0 rounded border border-global-primary bg-global-overlay peer-checked:bg-global-accent peer-checked:border-global-accent peer-focus-visible:ring-2 peer-focus-visible:ring-global-accent/40 transition-colors duration-200" />
                  <svg
                    className="absolute inset-0 w-full h-full text-white opacity-0 peer-checked:opacity-100 transition-opacity duration-200 pointer-events-none p-0.5"
                    fill="none"
                    viewBox="0 0 12 12"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M2 6l3 3 5-5"
                    />
                  </svg>
                </div>
                <span className="text-xs text-global-label group-hover:text-global-title transition-colors duration-200">
                  Mantener sesión iniciada
                </span>
              </label>

              <button
                type="submit"
                disabled={isPending || (requiresGymSelection && !gymSlug)}
                className="mt-1 w-full inline-flex items-center justify-center gap-2 rounded-lg btn-global-primary-gradient px-4 py-3 text-sm font-bold shadow-global-button focus:outline-none focus:ring-2 focus-global-ring focus:ring-offset-2 focus:ring-offset-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-200 touch-manipulation cursor-pointer uppercase tracking-wide"
              >
                {isPending ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4 shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Cargando...
                  </>
                ) : (
                  "Iniciar sesión"
                )}
              </button>
            </form>

            <p className="mt-5 text-center text-xs text-global-muted">
              ¿Eres entrenador?{" "}
              <Link
                to="/auth/trainer"
                className="text-global-accent hover:underline"
              >
                Acceso entrenadores
              </Link>
            </p>

            <p className="mt-6 flex items-center justify-center gap-1.5 text-xs text-global-muted md:hidden">
              <svg
                className="w-3.5 h-3.5 shrink-0 text-global-accent"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
              Tu seguridad es nuestra prioridad.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
